import { Dumbbell, Droplet, Timer, TrendingUp, UtensilsCrossed, CheckCircle2, ChefHat, Users, Camera, FileText } from "lucide-react";
import { FadeUp } from "./FadeUp";
import { CalorieRing } from "./CalorieRing";

const MACROS = [
  { label: "Protein", pct: 35, color: "#C6FF3D" },
  { label: "Carbs", pct: 45, color: "#A1A1AA" },
  { label: "Fat", pct: 20, color: "#5c6b2e" },
];

const SmallCard = ({ testid, icon: Icon, title, desc, tag, children }) => (
  <div
    data-testid={testid}
    className="relative h-full bg-[#141416] border border-[#232326] rounded-[24px] p-6 hover:border-[#C6FF3D]/40 hover:-translate-y-1 transition-all duration-300"
  >
    {tag && (
      <span className="absolute top-4 right-4 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-[#232326] text-[#A1A1AA]">
        {tag}
      </span>
    )}
    <Icon className="text-[#C6FF3D] mb-3" size={24} />
    <h3 className="text-white text-base font-semibold mb-2">{title}</h3>
    <p className="text-[#A1A1AA] text-xs mb-3">{desc}</p>
    {children}
  </div>
);

export const FeaturesBento = () => (
  <section id="features" className="py-14 md:py-20 px-6 bg-[#0A0A0B]">
    <div className="max-w-6xl mx-auto">
      <FadeUp>
        <h2 className="font-display text-4xl sm:text-5xl text-white text-center mb-10 md:mb-14">
          EVERYTHING YOU NEED IN ONE APP
        </h2>
      </FadeUp>

      <div className="grid md:grid-cols-4 gap-5 md:auto-rows-[170px]">
        <FadeUp className="md:col-span-2 md:row-span-2 md:col-start-1 md:row-start-1">
          <div
            data-testid="feature-card-ai-diet-plan"
            className="h-full bg-[#141416] border border-[#232326] rounded-[24px] p-8 hover:border-[#C6FF3D]/40 hover:-translate-y-1 transition-all duration-300 flex flex-col"
          >
            <UtensilsCrossed className="text-[#C6FF3D] mb-4" size={28} />
            <h3 className="text-white text-2xl font-semibold mb-2">AI Diet Plan</h3>
            <p className="text-[#A1A1AA] text-sm mb-5">
              Indian meals for breakfast, lunch, snacks and dinner — veg or non-veg, with calories
              and protein. Swap any meal anytime.
            </p>
            <div className="space-y-2 mb-5">
              {[
                ["Poha with peanuts", "320 kcal"],
                ["Dal + Roti + Sabzi", "480 kcal"],
                ["Paneer bhurji", "440 kcal"],
              ].map(([n, c]) => (
                <div key={n} className="flex justify-between bg-[#0A0A0B] border border-[#232326] rounded-xl px-4 py-2.5 text-sm">
                  <span className="text-white">{n}</span>
                  <span className="text-[#C6FF3D]">{c}</span>
                </div>
              ))}
            </div>
            <div className="mt-auto">
              <div className="flex h-2 rounded-full overflow-hidden mb-2">
                {MACROS.map((m) => (
                  <div key={m.label} style={{ width: `${m.pct}%`, backgroundColor: m.color }} />
                ))}
              </div>
              <div className="flex gap-4 text-xs text-[#A1A1AA]">
                {MACROS.map((m) => (
                  <span key={m.label} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                    {m.label} {m.pct}%
                  </span>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="md:col-span-2 md:row-span-2 md:col-start-3 md:row-start-1">
          <div
            data-testid="feature-card-gym-logger"
            className="h-full bg-[#141416] border border-[#232326] rounded-[24px] p-8 hover:border-[#C6FF3D]/40 hover:-translate-y-1 transition-all duration-300 flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Timer className="text-[#C6FF3D]" size={26} />
                <h3 className="text-white text-2xl font-semibold">Gym Workout Logger</h3>
              </div>
              <span className="font-display text-[#C6FF3D] text-lg">00:24:15</span>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-5">
              Start a workout timer, log weight and reps for every set, rest timer between sets,
              see what you lifted last time.
            </p>
            <div className="bg-[#0A0A0B] border border-[#232326] rounded-xl p-4 flex-1 flex flex-col">
              <div className="flex justify-between text-xs text-[#A1A1AA] mb-3">
                <span className="text-white font-semibold">Bench Press</span>
                <span className="text-[#C6FF3D]">Last: 60kg x 8</span>
              </div>
              <div className="space-y-2 flex-1">
                {[
                  { s: "Set 1 · 60kg x 8", done: true },
                  { s: "Set 2 · 62.5kg x 6", done: true },
                  { s: "Set 3 · 62.5kg x 5", done: false },
                ].map((row) => (
                  <div key={row.s} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 size={14} className={row.done ? "text-[#C6FF3D]" : "text-[#3a3a3e]"} />
                    <span className={row.done ? "text-white" : "text-[#A1A1AA]"}>{row.s}</span>
                  </div>
                ))}
              </div>
              <span className="self-start mt-3 bg-[#C6FF3D]/10 text-[#C6FF3D] text-xs font-semibold px-3 py-1.5 rounded-full">
                Rest · 00:45
              </span>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.2} className="md:col-start-1 md:row-start-3">
          <div
            data-testid="feature-card-ai-workout-plan"
            className="h-full bg-[#141416] border border-[#232326] rounded-[24px] p-6 hover:border-[#C6FF3D]/40 hover:-translate-y-1 transition-all duration-300"
          >
            <Dumbbell className="text-[#C6FF3D] mb-3" size={24} />
            <h3 className="text-white text-base font-semibold mb-2">AI Workout Plan</h3>
            <p className="text-[#A1A1AA] text-xs mb-3">Weekly split with sets and reps.</p>
            <div className="text-xs text-[#A1A1AA] flex gap-2 flex-wrap">
              {["Chest", "Back", "Legs"].map((d) => (
                <span key={d} className="bg-[#0A0A0B] border border-[#232326] rounded-full px-2.5 py-1">
                  {d}
                </span>
              ))}
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.3} className="md:col-start-2 md:row-start-3">
          <div
            data-testid="feature-card-daily-tracker"
            className="h-full bg-[#141416] border border-[#232326] rounded-[24px] p-6 hover:border-[#C6FF3D]/40 hover:-translate-y-1 transition-all duration-300"
          >
            <Droplet className="text-[#C6FF3D] mb-3" size={24} />
            <h3 className="text-white text-base font-semibold mb-2">Daily Tracker</h3>
            <p className="text-[#A1A1AA] text-xs mb-3">Tick meals, track water glasses.</p>
            <div className="flex gap-1.5">
              {Array.from({ length: 8 }).map((_, i) => (
                <Droplet key={i} size={14} className={i < 5 ? "text-[#C6FF3D]" : "text-[#3a3a3e]"} fill={i < 5 ? "#C6FF3D" : "none"} />
              ))}
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.4} className="md:col-span-2 md:col-start-3 md:row-start-3">
          <div
            data-testid="feature-card-progress"
            className="h-full bg-[#141416] border border-[#232326] rounded-[24px] p-6 hover:border-[#C6FF3D]/40 hover:-translate-y-1 transition-all duration-300 flex items-center gap-4"
          >
            <CalorieRing percent={74} label="74%" sub="goal" />
            <div>
              <TrendingUp className="text-[#C6FF3D] mb-2" size={22} />
              <h3 className="text-white text-base font-semibold mb-1">Progress</h3>
              <p className="text-[#A1A1AA] text-xs">Weight log, streaks and charts.</p>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.5} className="md:row-start-4">
          <SmallCard testid="feature-card-recipes" icon={ChefHat} title="Recipes & Grocery List" desc="Cook your meals with a weekly shopping list." tag="Pro" />
        </FadeUp>

        <FadeUp delay={0.55} className="md:row-start-4">
          <SmallCard testid="feature-card-community" icon={Users} title="Community Feed" desc="Share PRs and celebrate wins together." tag="Free" />
        </FadeUp>

        <FadeUp delay={0.6} className="md:row-start-4">
          <SmallCard testid="feature-card-checkins" icon={Camera} title="AI Physique Check-ins" desc="Upload photos, get AI feedback weekly." tag="Pro" />
        </FadeUp>

        <FadeUp delay={0.65} className="md:row-start-4">
          <SmallCard testid="feature-card-reports" icon={FileText} title="Progress Reports" desc="Monthly PDF with strength & heatmap." tag="Pro" />
        </FadeUp>
      </div>
    </div>
  </section>
);
